/**
 * SAID-DIIS — shared Avatar component.
 *
 * Renders the user's profile picture when `user.avatar_url` is set, otherwise
 * falls back to their initials in a teal circle. Used in every portal header
 * and in the Edit Profile modal preview.
 *
 * `size` is the pixel diameter of the circle. Initial-mode font sizing scales
 * proportionally so a 28px header-chip and a 96px modal-preview both read well.
 */
import React, { useMemo, useState } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function buildAvatarSrc(url) {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  // Backend returns path like /uploads/avatars/<file>. The serve route is
  // mounted under /api/uploads/... per route-prefix convention.
  const normalised = url.startsWith('/uploads/') ? `/api${url}` : url;
  return `${BACKEND_URL}${normalised}`;
}

function initialsOf(user) {
  const source = (user?.full_name || user?.username || '').trim();
  if (!source) return '·';
  return source
    .split(/\s+/)
    .map((s) => s.replace(/^[^A-Za-z]+/, ''))  // strip leading punctuation like '(Default)' → 'Default'
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0].toUpperCase())
    .join('') || source[0].toUpperCase();
}

export default function Avatar({ user, size = 32, className = '', style = {}, testId }) {
  const [failed, setFailed] = useState(false);
  const src = useMemo(() => buildAvatarSrc(user?.avatar_url), [user?.avatar_url]);
  const initials = useMemo(() => initialsOf(user), [user?.full_name, user?.username]);

  const base = {
    width: size,
    height: size,
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
    background: 'var(--diis-accent, #0f6b63)',
    color: 'white',
    fontFamily: 'var(--diis-font-sans)',
    fontWeight: 600,
    fontSize: Math.max(10, Math.round(size * 0.4)),
    letterSpacing: '0.02em',
    userSelect: 'none',
    ...style,
  };

  if (src && !failed) {
    return (
      <span
        className={className}
        style={base}
        data-testid={testId}
        aria-label={user?.full_name || user?.username || 'User'}
      >
        <img
          src={src}
          alt=""
          onError={() => setFailed(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      </span>
    );
  }

  return (
    <span
      className={className}
      style={base}
      data-testid={testId}
      aria-label={user?.full_name || user?.username || 'User'}
    >
      {initials}
    </span>
  );
}
